import { HOMEOWNER_PROBLEMS, IMAGES } from "@/lib/site";
import { Reveal, ParallaxImage } from "@/components/motion-primitives";
import { PageHero, CtaButton, CtaBand, SectionHead, usePageMeta } from "@/components/shared";
import ProfitCalculator from "@/components/ProfitCalculator";

export default function ForHomeowners() {
  usePageMeta("For Homeowners", "Your Bermuda home deserves more than a listing. Local, professional vacation rental management that protects your investment and your guests' experience.");
  return (
    <main>
      <PageHero
        overline="For Homeowners"
        lines={["Your Home Deserves", "More Than a Listing."]}
        copy="Anyone can publish a property online. Protecting your investment — and the experience of every guest who walks through your door — takes a local, professional, accountable partner."
        image={IMAGES.ocean}
      >
        <CtaButton to="/assessment" variant="accent" testid="homeowners-assessment-btn">Get a Property Assessment</CtaButton>
      </PageHero>

      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-32">
        <SectionHead
          overline="Sound Familiar?"
          title="Every owner's frustration has a local solution."
          copy="These are the exact problems Bermuda homeowners bring to us — and exactly how each one disappears."
        />
        <div className="mt-16">
          {HOMEOWNER_PROBLEMS.map(([problem, solution], i) => (
            <Reveal key={problem} delay={0.03}>
              <div className="group grid gap-4 border-t border-navy/10 py-10 transition-colors duration-500 last:border-b hover:bg-sand-light/50 md:grid-cols-12 md:gap-10 md:px-6">
                <div className="md:col-span-1">
                  <span className="font-mono text-xs text-bermuda-dark">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-serif text-2xl leading-snug text-navy md:col-span-5 lg:text-[28px]">"{problem}"</h3>
                <p className="text-sm leading-relaxed text-navy/65 md:col-span-6 md:pt-1.5">{solution}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <ProfitCalculator />

      <section className="relative overflow-hidden bg-navy text-ivory">
        <div className="absolute inset-y-0 right-0 hidden w-1/2 lg:block">
          <ParallaxImage src={IMAGES.pinkSand} alt="Bermuda pink sand beach" className="h-full" speed={0.1} />
          <div className="absolute inset-0 bg-gradient-to-r from-navy to-navy/20" />
        </div>
        <div className="noise-overlay relative z-10 mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-36">
          <div className="max-w-xl">
            <SectionHead
              dark
              overline="The Partnership Promise"
              title="Ownership stays with you. The weight comes off you."
              copy="You keep your listing, your account, your payouts and full visibility of everything we do. We carry the operations: the guests, the cleaners, the contractors, the inspections, the 11pm messages. That is what a property partner means."
            />
            <Reveal delay={0.25}>
              <div className="mt-10 flex flex-wrap gap-4">
                <CtaButton to="/assessment" variant="accent" testid="homeowners-partner-assessment-btn">Get a Property Assessment</CtaButton>
                <CtaButton to="/pricing" variant="outline" dark testid="homeowners-pricing-btn">See Pricing</CtaButton>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBand title="Let's protect your investment properly." />
    </main>
  );
}
